import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Editor from './CodeEditor';
import { getErrorMessage, submissionService } from '../services/api';
import type { SubmissionWithStudent } from '../types/api';
import MetricsDisplay from './MetricsDisplay';
import StatusBadge from './StatusBadge';
import { useToast } from './Toast';
import { EmptyState, LoadingState, Modal } from './ui';
import { useTheme } from '../contexts/ThemeContext';
import { formatDateTime } from '../utils/format';
import {
  X,
  Clock,
  Share2,
  MessageSquare,
  Pencil,
  BookOpen,
  Eye,
  ChevronDown,
  ChevronLeft,
  Play
} from 'lucide-react';

interface SubmissionsPanelProps {
  classId: number;
  className: string;
  lessons: Array<{ id: number; title: string }>;
  onClose: () => void;
}

const SubmissionsPanel: React.FC<SubmissionsPanelProps> = ({ classId, className, lessons, onClose }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isDarkMode } = useTheme();
  // Start on the first lesson rather than an empty page.
  const [selectedLesson, setSelectedLesson] = useState<number | null>(lessons[0]?.id ?? null);
  // Phones show one column at a time: the lesson list, or the submissions of the chosen lesson.
  const [showListOnPhone, setShowListOnPhone] = useState(true);
  const [submissions, setSubmissions] = useState<SubmissionWithStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionWithStudent | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const monacoTheme = isDarkMode ? 'vs-dark' : 'vs';
  // Only the latest request may fill the list: switching lessons quickly must not show another lesson's work.
  const requestRef = useRef(0);

  const loadSubmissions = useCallback(async (lessonId: number | null) => {
    const requestId = ++requestRef.current;
    if (!lessonId) {
      setSubmissions([]);
      return;
    }

    setLoading(true);
    try {
      const data = await submissionService.forClassLesson(classId, lessonId);
      if (requestId === requestRef.current) {
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      if (requestId === requestRef.current) {
        setSubmissions([]);
        toast.error(`Submissions could not be loaded: ${getErrorMessage(error)}`);
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [classId, toast]);

  useEffect(() => {
    loadSubmissions(selectedLesson);
  }, [selectedLesson, loadSubmissions]);

  useEffect(() => {
    if (selectedLesson === null && lessons.length > 0) {
      setSelectedLesson(lessons[0].id);
    }
  }, [lessons, selectedLesson]);

  const handleReviewSubmit = async () => {
    if (!selectedSubmission || !reviewText.trim()) return;

    setSubmittingReview(true);
    try {
      await submissionService.review(selectedSubmission.id, reviewText);

      // Reload submissions to get updated review
      await loadSubmissions(selectedLesson);
      setReviewText('');
      setSelectedSubmission(null);
      toast.success('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(`Failed to submit review: ${getErrorMessage(error)}`);
    } finally {
      setSubmittingReview(false);
    }
  };

  const copyShareLink = async (shareToken: string) => {
    const link = `${window.location.origin}/submission/${shareToken}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Share link copied to clipboard!');
    } catch {
      toast.error('Unable to copy. Please copy the link manually.');
    }
  };

  const selectedLessonTitle = lessons.find((lesson) => lesson.id === selectedLesson)?.title;

  return (
    <div className="modal-backdrop p-2 sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="submissions-panel-title" className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl h-[95vh] sm:h-[85vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-800">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h2 id="submissions-panel-title" className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
              Review Submissions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{className}</p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon shrink-0 [&_svg]:w-5 [&_svg]:h-5"
            aria-label="Close"
          >
            <X />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left sidebar - Lesson selector */}
          <div className={`${showListOnPhone ? 'block' : 'hidden'} md:block w-full md:w-72 md:border-r border-gray-200 dark:border-gray-800 overflow-y-auto p-3 sm:p-4 bg-gray-50 dark:bg-gray-950/40`}>
            <h3 className="eyebrow mb-3 px-2">
              Select Lesson
            </h3>
            {lessons.length === 0 && (
              <p className="px-2 text-sm text-gray-500 dark:text-gray-400">This class has no lessons yet.</p>
            )}
            <div className="space-y-1">
              {lessons.map(lesson => (
                <button
                  key={lesson.id}
                  onClick={() => {
                    setSelectedLesson(lesson.id);
                    setShowListOnPhone(false);
                  }}
                  aria-current={selectedLesson === lesson.id ? 'true' : undefined}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-2.5 ${selectedLesson === lesson.id
                    ? 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 font-semibold shadow-sm ring-1 ring-gray-200 dark:ring-gray-700'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-800/60'
                    }`}
                >
                  <BookOpen className={`w-4 h-4 shrink-0 ${selectedLesson === lesson.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                  <span className="truncate text-sm">{lesson.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main content area */}
          <div className={`${showListOnPhone ? 'hidden' : 'block'} md:block flex-1 min-w-0 overflow-y-auto p-3 sm:p-6 bg-white dark:bg-gray-900`}>
            <button
              onClick={() => setShowListOnPhone(true)}
              className="md:hidden mb-4 link flex items-center gap-1 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Lessons
            </button>
            {selectedLessonTitle && (
              <h3 className="md:hidden mb-4 font-bold text-gray-900 dark:text-white">{selectedLessonTitle}</h3>
            )}
            {!selectedLesson ? (
              <EmptyState icon={<BookOpen />} title="Select a lesson to view submissions" />
            ) : loading ? (
              <LoadingState label="Loading submissions..." />
            ) : submissions.length === 0 ? (
              <EmptyState icon={<Clock />} title="No submissions for this lesson yet">
                Submissions appear here as soon as students send them.
              </EmptyState>
            ) : (
              <div className="space-y-4">
                {submissions.map(submission => (
                  <div
                    key={submission.id}
                    className="card p-4 sm:p-5 group"
                  >
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="avatar" aria-hidden="true">
                            {submission.student_name.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-gray-900 dark:text-white tracking-tight truncate">
                              {submission.student_name}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {submission.student_email}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                          <StatusBadge status={submission.status} />
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                            <Clock className="w-3.5 h-3.5 shrink-0" />
                            {formatDateTime(submission.submitted_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => copyShareLink(submission.share_token)}
                          className="btn btn-secondary btn-sm"
                          title="Copy share link"
                        >
                          <Share2 className="w-4 h-4 shrink-0" />
                          <span>Copy Link</span>
                        </button>
                        <button
                          onClick={() => navigate(`/lesson/${submission.lesson_id}?class=${classId}`, { state: { initialCode: submission.code } })}
                          className="btn btn-success btn-sm"
                          title="Run simulation with this submission's code"
                        >
                          <Play className="w-4 h-4 shrink-0" />
                          <span>Run Simulation</span>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setReviewText(submission.teacher_review || '');
                          }}
                          className={`btn btn-sm ${submission.teacher_review ? 'btn-secondary' : 'btn-primary'}`}
                        >
                          <Pencil className="w-4 h-4 shrink-0" />
                          <span>{submission.teacher_review ? 'Edit Review' : 'Add Review'}</span>
                        </button>
                      </div>
                    </div>

                    {submission.teacher_review && (
                      <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900/60 rounded-xl p-4 mb-3">
                        <p className="flex items-center gap-2 text-sm font-semibold text-violet-900 dark:text-violet-200 mb-2">
                          <MessageSquare className="w-4 h-4 shrink-0" /> Your Review:
                        </p>
                        <p className="text-sm text-violet-900/90 dark:text-violet-100 whitespace-pre-wrap leading-relaxed">{submission.teacher_review}</p>
                        {submission.reviewed_at && (
                          <p className="text-xs text-violet-700 dark:text-violet-300 mt-2">
                            Reviewed on {formatDateTime(submission.reviewed_at)}
                          </p>
                        )}
                      </div>
                    )}

                    {submission.metrics && typeof submission.metrics === 'object' && Object.keys(submission.metrics).length > 0 && (
                      <MetricsDisplay metrics={submission.metrics} title="Simulation Metrics" className="mb-3" />
                    )}

                    <details className="mt-3 group">
                      <summary className="cursor-pointer link text-sm flex items-center gap-1 list-none w-fit">
                        <Eye className="w-4 h-4 group-open:hidden" />
                        <ChevronDown className="w-4 h-4 hidden group-open:block" />
                        <span>View Code</span>
                      </summary>
                      <div className="mt-2 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden" style={{ height: '300px' }}>
                        <Editor
                          height="100%"
                          defaultLanguage="python"
                          value={submission.code}
                          theme={monacoTheme}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            fontSize: 12,
                            scrollBeyondLastLine: false,
                          }}
                        />
                      </div>
                    </details>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {selectedSubmission && (
        <Modal
          title="Submit Student Review"
          description={`Reviewing submission from ${selectedSubmission.student_name}`}
          size="lg"
          zIndex="z-[60]"
          onClose={() => {
            setSelectedSubmission(null);
            setReviewText('');
          }}
          footer={
            <>
              <button
                onClick={() => {
                  setSelectedSubmission(null);
                  setReviewText('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReviewSubmit}
                disabled={submittingReview || !reviewText.trim()}
                className="btn btn-primary"
              >
                {submittingReview ? 'Submitting...' : 'Confirm Review'}
              </button>
            </>
          }
        >
          <label htmlFor="review-text" className="label">Feedback & Assessment</label>
          <textarea
            id="review-text"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            className="input resize-y"
            rows={8}
            placeholder="Provide constructive feedback for the student..."
          />
        </Modal>
      )}
    </div>
  );
};

export default SubmissionsPanel;
